import { useCallback, useEffect, useMemo, useReducer, useRef, useState } from 'react'
import { useSelector } from 'react-redux'
import { clsx } from 'clsx'

import { ActionBar } from './components/ActionBar'
import { ArtifactChangedCallout } from './components/ArtifactChangedCallout'
import { AssignSpeakerDialog } from './components/AssignSpeakerDialog'
import { ConflictDialog } from './components/ConflictDialog'
import { FinalizedSourceCallout } from './components/FinalizedSourceCallout'
import { GtContextStrip } from './components/GtContextStrip'
import { GtHeader } from './components/GtHeader'
import { type GtRowEditor, GtTurnList } from './components/GtTurnList'
import { InlineTextField } from './components/InlineTextField'
import { KeywordAssist } from './components/KeywordAssist'
import { MirrorRestoreCallout } from './components/MirrorRestoreCallout'
import { ReadOnlyCallout } from './components/ReadOnlyCallout'
import { SeedStatus } from './components/SeedStatus'
import { seedStateOf } from './seedState'
import { RenameDialog } from './components/RenameDialog'
import { RoomDialog, type RoomDialogMode } from './components/RoomDialog'
import { RosterBar } from './components/RosterBar'
import { ShortcutsDialog } from './components/ShortcutsDialog'
import {
  deriveAttentionTurnIds,
  deriveFinalizeBlockers,
  deriveProgress,
  deriveUnreviewedTurnIds,
  hotkeySpeakerMap,
  liveTurns,
  liveTurnsOwnedBy,
  speakersById,
} from './gtDerivations'
import { nextStop, offsetToMs, turnStopOffsets } from './gtEmphasis'
import {
  canGroupIntoRoom,
  caretSplit,
  displaySpeakerName,
  GT_NOTE_MAX_CODEPOINTS,
  GT_ROSTER_MAX,
  type GtDoc,
  type GtLabel,
  gtReducer,
  hydrateDoc,
  initialGtState,
  labelOfSpeaker,
  MAX_TURN_TEXT_CODEPOINTS,
  playheadCaret,
  roomDepartureHasNameCollision,
  roomsById,
  roomVoiceOrdinals,
  splitTurnIds,
} from './gtModel'
import { useGtArtifacts } from './useGtArtifacts'
import { useGtAudio } from './useGtAudio'
import { clearGtMirror, findGtMirror, readGtMirror, useGtDocument } from './useGtDocument'
import { useGtFinalize } from './useGtFinalize'
import { type GtHotkeyHandlers, useGtHotkeys } from './useGtHotkeys'
import { useGtKeywords } from './useGtKeywords'

import styles from './AnnotateMode.module.scss'

import { isApiError } from '@/api'
import type { GtTurn, MeetingAttributionGtPrefillResponse } from '@/api/internal'
import Button from '@/components/Button'
import ConfirmDialog from '@/components/ConfirmDialog/ConfirmDialog'
import { Switch } from '@/components/Switch'
import { useAttributionGtPrefill, useDiscardAttributionGt } from '@/hooks/useAttributionGt'
import { selectEmail } from '@/store/features/user/userSlice'

const CLIP_PAD_MS = 1000
const SCRUB_RATE = 3
const SCRUB_TICK_MS = 100

type Scrub = { direction: 1 | -1; rate: number; timer: number | null }
const DISCARD_STALE_MESSAGE =
  'Someone saved a newer draft while this page was open. Showing that draft instead of starting over.'

type EditMode = 'text' | 'note' | 'split' | null
const EDIT_MODE_LABEL: Record<NonNullable<EditMode>, string> = { text: 'Edit text', note: 'Note', split: 'Split text' }

const turnAtLocalTime = (turns: readonly GtTurn[], ms: number): GtTurn | null => {
  let match: GtTurn | null = null
  for (let i = 0; i < turns.length; i += 1) {
    if (turns[i].start_ms > ms) break
    match = turns[i]
  }
  return match ?? turns[0] ?? null
}

export const AnnotateMode = ({ meetingId }: { meetingId: string }) => {
  const reviewerEmail = useSelector(selectEmail)
  const prefillQuery = useAttributionGtPrefill(meetingId)
  const keywordAssist = useGtKeywords(meetingId)
  const [state, dispatch] = useReducer(gtReducer, initialGtState())
  const hydratedRef = useRef(false)

  const [activeTurnId, setActiveTurnId] = useState<string | null>(null)
  const [focusedStop, setFocusedStop] = useState<{ turnId: string; offset: number } | null>(null)
  const [needsAttentionOnly, setNeedsAttentionOnly] = useState(false)
  const [unreviewedOnly, setUnreviewedOnly] = useState(false)
  const [autoAdvance, setAutoAdvance] = useState(true)
  const [editMode, setEditMode] = useState<EditMode>(null)
  const [editCaret, setEditCaret] = useState<number | null>(null)
  const textEditing = editMode === 'text' || editMode === 'split'
  const [rosterAdding, setRosterAdding] = useState(false)
  const [renameSpeakerId, setRenameSpeakerId] = useState<string | null>(null)
  const [assignTurnId, setAssignTurnId] = useState<string | null>(null)
  const [selectedSpeakerIds, setSelectedSpeakerIds] = useState<ReadonlySet<string>>(() => new Set())
  const [roomDialog, setRoomDialog] = useState<RoomDialogMode | null>(null)
  const [deleteSpeakerIds, setDeleteSpeakerIds] = useState<string[] | null>(null)
  const [shortcutsOpen, setShortcutsOpen] = useState(false)
  const [mirrorPrompt, setMirrorPrompt] = useState<{ version: number; doc: GtDoc; sameVersion: boolean } | null>(null)

  const hydrateFromPrefill = useCallback(
    (prefill: MeetingAttributionGtPrefillResponse) => {
      const documentResponse = prefill.document
      const artifactChangedNow =
        prefill.current_refined_raw_sha256 !== null &&
        prefill.current_refined_raw_sha256 !== documentResponse.refined_raw_sha256
      const readOnly = !prefill.annotatable || artifactChangedNow
      const loadedDoc = hydrateDoc(documentResponse.document)
      const version = documentResponse.version
      dispatch({
        type: 'hydrate',
        doc: loadedDoc,
        version,
        sha: documentResponse.document_sha256,
        turnsSnapshot: documentResponse.turns_snapshot,
        readOnly,
      })
      hydratedRef.current = true

      if (readOnly) {
        setMirrorPrompt(null)
        return
      }
      const sameVersionMirror = readGtMirror(meetingId, version)
      if (sameVersionMirror && JSON.stringify(sameVersionMirror.doc) !== JSON.stringify(loadedDoc)) {
        setMirrorPrompt({ version, doc: sameVersionMirror.doc, sameVersion: true })
        return
      }
      const otherVersionMirror = findGtMirror(meetingId)
      if (otherVersionMirror && otherVersionMirror.version !== version) {
        setMirrorPrompt({
          version: otherVersionMirror.version,
          doc: otherVersionMirror.mirror.doc,
          sameVersion: false,
        })
        return
      }
      setMirrorPrompt(null)
    },
    [meetingId]
  )

  const seedState = seedStateOf(prefillQuery.data?.annotatable_reason)

  useEffect(() => {
    if (!hydratedRef.current && prefillQuery.data && seedState === null) hydrateFromPrefill(prefillQuery.data)
  }, [prefillQuery.data, seedState, hydrateFromPrefill])

  const artifactChanged = Boolean(
    prefillQuery.data &&
    prefillQuery.data.current_refined_raw_sha256 !== null &&
    prefillQuery.data.current_refined_raw_sha256 !== prefillQuery.data.document.refined_raw_sha256
  )

  const onAdopt = useCallback((version: number, sha: string, editSeq: number) => {
    dispatch({ type: 'markSaved', version, sha, editSeq })
  }, [])

  const { refetch: refetchPrefill } = prefillQuery
  const onReloadTheirs = useCallback(() => {
    hydratedRef.current = false
    void refetchPrefill().then((result) => {
      if (result.data) hydrateFromPrefill(result.data)
    })
  }, [refetchPrefill, hydrateFromPrefill])

  const onMirrorRestore = useCallback(() => {
    if (!mirrorPrompt) return
    dispatch({ type: 'restoreMirror', doc: mirrorPrompt.doc })
    clearGtMirror(meetingId, mirrorPrompt.version)
    setMirrorPrompt(null)
  }, [mirrorPrompt, meetingId])

  const onMirrorDiscard = useCallback(() => {
    if (!mirrorPrompt) return
    clearGtMirror(meetingId, mirrorPrompt.version)
    setMirrorPrompt(null)
  }, [mirrorPrompt, meetingId])

  const gtDocument = useGtDocument({
    meetingId,
    doc: state.doc,
    version: state.version,
    dirty: state.dirty,
    editSeq: state.editSeq,
    readOnly: state.readOnly,
    prefillVersion: prefillQuery.data?.prefill_version ?? '',
    refinedRawSha256: prefillQuery.data?.refined_raw_sha256 ?? '',
    onAdopt,
    onReloadTheirs,
  })
  const gtDocumentRef = useRef(gtDocument)
  gtDocumentRef.current = gtDocument

  const { mutateAsync: discardDraft, isPending: discardPending } = useDiscardAttributionGt(meetingId)
  const [discardConfirmOpen, setDiscardConfirmOpen] = useState(false)
  const [discardError, setDiscardError] = useState<string | null>(null)
  const onDiscardConfirm = useCallback(() => {
    gtDocumentRef.current.discardLocal()
    discardDraft(state.version)
      .then(() => onReloadTheirs())
      .catch((error: unknown) => {
        if (isApiError(error) && error.status === 404) {
          onReloadTheirs()
          return
        }
        if (isApiError(error) && error.status === 409) {
          onReloadTheirs()
          setDiscardError(DISCARD_STALE_MESSAGE)
          return
        }
        gtDocumentRef.current.retry()
        setDiscardError(isApiError(error) ? error.message : "Couldn't discard the draft")
      })
      .finally(() => setDiscardConfirmOpen(false))
  }, [discardDraft, onReloadTheirs, state.version])

  const progress = useMemo(() => deriveProgress(state.doc), [state.doc])
  const blockers = useMemo(() => deriveFinalizeBlockers(state.doc), [state.doc])

  const finalize = useGtFinalize({
    meetingId,
    saveStatus: gtDocument.status,
    blockers,
    dirty: state.dirty,
    baselineSha: state.baselineSha,
    finalizedGeneration: prefillQuery.data?.finalized_generation ?? null,
    finalizedSha: prefillQuery.data?.finalized_document_sha256 ?? null,
    settle: gtDocument.settle,
    onFinalized: onReloadTheirs,
  })

  const attentionTurnIds = useMemo(() => deriveAttentionTurnIds(state.doc, blockers), [state.doc, blockers])
  const unreviewedTurnIds = useMemo(() => deriveUnreviewedTurnIds(state.doc), [state.doc])
  const orderedTurns = useMemo(() => {
    if (!needsAttentionOnly && !unreviewedOnly) return state.doc.turns
    return state.doc.turns.filter(
      (turn) =>
        (!needsAttentionOnly || attentionTurnIds.has(turn.id)) && (!unreviewedOnly || unreviewedTurnIds.has(turn.id))
    )
  }, [state.doc.turns, needsAttentionOnly, attentionTurnIds, unreviewedOnly, unreviewedTurnIds])

  useEffect(() => {
    if (orderedTurns.length === 0) {
      setActiveTurnId(null)
      return
    }
    if (!orderedTurns.some((turn) => turn.id === activeTurnId)) {
      setActiveTurnId(orderedTurns[0].id)
    }
  }, [orderedTurns, activeTurnId])

  const bySpeakerId = useMemo(() => speakersById(state.doc.speakers), [state.doc.speakers])
  const hotkeySpeakers = useMemo(() => hotkeySpeakerMap(state.doc.speakers), [state.doc.speakers])
  const displayNames = useMemo(() => {
    const rooms = roomsById(state.doc)
    const ordinals = roomVoiceOrdinals(state.doc)
    return new Map(state.doc.speakers.map((sp) => [sp.id, displaySpeakerName(sp, rooms, ordinals)]))
  }, [state.doc])

  // Undo, break-up and merges can drop a ticked speaker; only ids still on the roster count as selected.
  const selectedRosterIds = useMemo(
    () => new Set(Array.from(selectedSpeakerIds).filter((id) => bySpeakerId.has(id))),
    [selectedSpeakerIds, bySpeakerId]
  )
  const onToggleSelect = useCallback(
    (speakerId: string) => {
      const speaker = bySpeakerId.get(speakerId)
      if (!speaker || labelOfSpeaker(speaker) !== null) return
      setSelectedSpeakerIds((ids) => {
        const next = new Set(ids)
        if (next.has(speakerId)) next.delete(speakerId)
        else next.add(speakerId)
        return next
      })
    },
    [bySpeakerId]
  )

  const turnCounts = useMemo(() => {
    const counts = new Map<string, number>()
    for (const turn of liveTurns(state.doc)) {
      if (!turn.speaker_id) continue
      counts.set(turn.speaker_id, (counts.get(turn.speaker_id) ?? 0) + 1)
    }
    return counts
  }, [state.doc])

  const turnsById = useMemo(() => new Map(state.doc.turns.map((turn) => [turn.id, turn])), [state.doc.turns])
  const activeTurn = activeTurnId ? (turnsById.get(activeTurnId) ?? null) : null
  const assignTurn = assignTurnId ? (turnsById.get(assignTurnId) ?? null) : null
  const activeTurnRef = useRef(activeTurn)
  activeTurnRef.current = activeTurn
  const activeSpeaker = activeTurn?.speaker_id ? (bySpeakerId.get(activeTurn.speaker_id) ?? null) : null
  const assignSpeaker = assignTurn?.speaker_id ? (bySpeakerId.get(assignTurn.speaker_id) ?? null) : null
  const activeSnapshot = activeTurn ? (state.snapshotByTurnId[activeTurn.source_turn_id] ?? null) : null

  const audio = useGtAudio(meetingId, Boolean(activeTurn))
  const audioRef = useRef(audio)
  audioRef.current = audio
  const stopsByTurnIndex = useMemo(
    () =>
      orderedTurns.map((turn) =>
        turnStopOffsets(turn.text, keywordAssist.keywordsBySourceTurnId[turn.source_turn_id] ?? [])
      ),
    [orderedTurns, keywordAssist.keywordsBySourceTurnId]
  )
  const playPadded = useCallback(
    (turn: GtTurn) => audioRef.current.playFrom(Math.max(0, turn.start_ms - CLIP_PAD_MS), turn.end_ms + CLIP_PAD_MS),
    []
  )
  const artifacts = useGtArtifacts(meetingId, state.doc, {
    prefillVersion: prefillQuery.data?.prefill_version ?? '',
    rawSha256: prefillQuery.data?.refined_raw_sha256 ?? '',
    prefillOrigin: prefillQuery.data?.document.prefill_origin ?? 'soniox',
  })
  const pinnedTimeRef = useRef<number | null>(null)
  const orderedTurnsRef = useRef(orderedTurns)
  orderedTurnsRef.current = orderedTurns
  const selectedBySeekRef = useRef(false)
  const focusedByJumpRef = useRef(false)

  const onSeek = useCallback((ms: number) => {
    pinnedTimeRef.current = ms
    setFocusedStop(null)
    if (audioRef.current.state === 'present') audioRef.current.seekTo(ms)
    const turn = turnAtLocalTime(orderedTurnsRef.current, ms)
    if (turn && turn.id !== activeTurnRef.current?.id) {
      selectedBySeekRef.current = true
      setActiveTurnId(turn.id)
    }
  }, [])

  const navigate = useCallback(
    (direction: 1 | -1) => {
      if (orderedTurns.length === 0) return
      const index = orderedTurns.findIndex((turn) => turn.id === activeTurnId)
      const nextIndex = ((index < 0 ? 0 : index) + direction + orderedTurns.length) % orderedTurns.length
      // Arrowing is how most reviewers accept a turn: leaving an unreviewed turn confirms its prefilled speaker.
      const departed = index >= 0 ? orderedTurns[index] : null
      if (departed && !state.readOnly && departed.state === null && departed.speaker_id) {
        dispatch({
          type: 'setState',
          turnId: departed.id,
          state: 'confirmed',
          by: reviewerEmail || 'unknown',
          at: new Date().toISOString(),
        })
      }
      setActiveTurnId(orderedTurns[nextIndex].id)
    },
    [orderedTurns, activeTurnId, state.readOnly, reviewerEmail]
  )

  const advanceAfterDecision = useCallback(
    (decidedId: string) => {
      if (!autoAdvance || orderedTurns.length === 0) return
      const index = Math.max(
        0,
        orderedTurns.findIndex((turn) => turn.id === decidedId)
      )
      setActiveTurnId(orderedTurns[(index + 1) % orderedTurns.length].id)
    },
    [autoAdvance, orderedTurns]
  )

  const applyState = useCallback(
    (turnState: 'confirmed' | 'deleted') => {
      if (!activeTurnId) return
      dispatch({
        type: 'setState',
        turnId: activeTurnId,
        state: turnState,
        by: reviewerEmail || 'unknown',
        at: new Date().toISOString(),
      })
      advanceAfterDecision(activeTurnId)
    },
    [activeTurnId, reviewerEmail, advanceAfterDecision]
  )

  const onConfirm = useCallback(() => applyState('confirmed'), [applyState])

  const focusTurn = useCallback(
    (turnId: string) => {
      if (!orderedTurns.some((turn) => turn.id === turnId)) {
        setNeedsAttentionOnly(false)
        setUnreviewedOnly(false)
      }
      setActiveTurnId(turnId)
    },
    [orderedTurns]
  )
  const onDelete = useCallback(() => applyState('deleted'), [applyState])

  const onClear = useCallback(() => {
    if (!activeTurnId) return
    dispatch({
      type: 'setState',
      turnId: activeTurnId,
      state: null,
      by: reviewerEmail || 'unknown',
      at: new Date().toISOString(),
    })
  }, [activeTurnId, reviewerEmail])

  const onAssignToTurn = useCallback(
    (turnId: string, speakerId: string) => {
      dispatch({ type: 'setSpeaker', turnId, speakerId })
      dispatch({
        type: 'setState',
        turnId,
        state: 'confirmed',
        by: reviewerEmail || 'unknown',
        at: new Date().toISOString(),
      })
      advanceAfterDecision(turnId)
    },
    [reviewerEmail, advanceAfterDecision]
  )

  const onAssign = useCallback(
    (speakerId: string) => {
      if (activeTurnId) onAssignToTurn(activeTurnId, speakerId)
    },
    [activeTurnId, onAssignToTurn]
  )

  const onLabel = useCallback(
    (label: GtLabel) => {
      if (!activeTurnId) return
      const speakers = state.doc.speakers
      if (speakers.length >= GT_ROSTER_MAX && !speakers.some((sp) => labelOfSpeaker(sp) === label)) return
      dispatch({
        type: 'assignLabel',
        turnId: activeTurnId,
        label,
        by: reviewerEmail || 'unknown',
        at: new Date().toISOString(),
      })
      advanceAfterDecision(activeTurnId)
    },
    [activeTurnId, reviewerEmail, advanceAfterDecision, state.doc.speakers]
  )

  const onFix = useCallback(
    (hotkey: string) => {
      const speaker = hotkeySpeakers.get(hotkey)
      if (!speaker) return
      onAssign(speaker.id)
    },
    [hotkeySpeakers, onAssign]
  )

  const dropSelectedIds = useCallback((ids: readonly string[]) => {
    const wanted = new Set(ids)
    setSelectedSpeakerIds((selected) => new Set(Array.from(selected).filter((id) => !wanted.has(id))))
  }, [])

  const requestDeleteSpeakers = useCallback(
    (ids: string[]) => {
      if (state.readOnly || ids.length === 0) return
      if (liveTurnsOwnedBy(state.doc, ids) === 0) {
        dispatch({ type: 'deleteSpeakers', speakerIds: ids })
        dropSelectedIds(ids)
        return
      }
      setDeleteSpeakerIds(ids)
    },
    [state.readOnly, state.doc, dropSelectedIds]
  )

  const onDeleteSpeakersConfirm = useCallback(() => {
    if (!deleteSpeakerIds) return
    dispatch({ type: 'deleteSpeakers', speakerIds: deleteSpeakerIds })
    dropSelectedIds(deleteSpeakerIds)
    setDeleteSpeakerIds(null)
  }, [deleteSpeakerIds, dropSelectedIds])

  const openGroupDialog = useCallback(
    (speakerIds: readonly string[]) => {
      const speakers = speakerIds.flatMap((id) => {
        const speaker = bySpeakerId.get(id)
        return speaker && labelOfSpeaker(speaker) === null ? [speaker] : []
      })
      if (canGroupIntoRoom(speakers.length, state.doc.rooms.length)) setRoomDialog({ type: 'group', speakers })
    },
    [bySpeakerId, state.doc.rooms.length]
  )
  const openRenameRoomDialog = useCallback(
    (roomId: string) => {
      const room = state.doc.rooms.find((r) => r.id === roomId)
      if (room) setRoomDialog({ type: 'rename', room })
    },
    [state.doc.rooms]
  )
  /** G: group selected voices, or edit the selected/active speaker. */
  const onRoom = useCallback(() => {
    if (state.readOnly) return
    if (selectedRosterIds.size > 1) {
      openGroupDialog(Array.from(selectedRosterIds))
      return
    }
    const speakerId =
      selectedRosterIds.size === 1 ? Array.from(selectedRosterIds)[0] : activeTurnRef.current?.speaker_id
    const speaker = speakerId ? bySpeakerId.get(speakerId) : undefined
    if (!speaker || labelOfSpeaker(speaker) !== null) return
    setRenameSpeakerId(speaker.id)
  }, [state.readOnly, selectedRosterIds, bySpeakerId, openGroupDialog])
  const onRoomSubmit = useCallback(
    (label: string, voiceNames?: Record<string, string | null>) => {
      if (!roomDialog) return
      if (roomDialog.type === 'rename') {
        dispatch({ type: 'renameRoom', roomId: roomDialog.room.id, label })
        return
      }
      dispatch({ type: 'groupRoom', speakerIds: roomDialog.speakers.map((sp) => sp.id), label, voiceNames })
      setSelectedSpeakerIds(new Set())
    },
    [roomDialog]
  )

  const textFieldRef = useRef<HTMLTextAreaElement | null>(null)
  const splitAtCaret = useCallback((): boolean => {
    const field = textFieldRef.current
    if (!activeTurn || !field) return false
    const plan = caretSplit(activeTurn, field.value, field.selectionStart ?? 0)
    if (!plan) return false
    const ids = splitTurnIds(state.doc.turns, activeTurn.source_turn_id)
    dispatch({ type: 'split', turnId: activeTurn.id, atMs: plan.atMs, texts: plan.texts, ids })
    setEditMode(null)
    setActiveTurnId(ids[1])
    return true
  }, [activeTurn, state.doc.turns])

  const startTextEdit = useCallback((turnId: string, caret: number | null = null) => {
    setActiveTurnId(turnId)
    setEditCaret(caret)
    setEditMode('text')
  }, [])

  const scrubRef = useRef<Scrub | null>(null)

  const onScrubEnd = useCallback(() => {
    const scrub = scrubRef.current
    if (!scrub) return
    scrubRef.current = null
    if (scrub.timer != null) window.clearInterval(scrub.timer)
    audioRef.current.setPlaybackRate(scrub.rate)
    audioRef.current.pause()
    pinnedTimeRef.current = audioRef.current.audioTime
  }, [])

  const onScrub = useCallback(
    (direction: 1 | -1) => {
      const turn = activeTurnRef.current
      if (!turn || audioRef.current.state !== 'present' || scrubRef.current?.direction === direction) return
      const rate = scrubRef.current?.rate ?? audioRef.current.playbackRate
      onScrubEnd()
      const now = audioRef.current.audioTime ?? turn.start_ms
      const from = Math.min(Math.max(now, turn.start_ms), turn.end_ms)
      const scrub: Scrub = { direction, rate, timer: null }
      scrubRef.current = scrub
      if (direction === 1) {
        audioRef.current.setPlaybackRate(SCRUB_RATE)
        audioRef.current.playFrom(from >= turn.end_ms ? turn.start_ms : from, turn.end_ms)
        return
      }
      audioRef.current.pause()
      let position = from
      const stepBack = () => {
        position = Math.max(turn.start_ms, position - SCRUB_RATE * SCRUB_TICK_MS)
        audioRef.current.seekTo(position)
        if (position === turn.start_ms && scrub.timer != null) {
          window.clearInterval(scrub.timer)
          scrub.timer = null
        }
      }
      stepBack()
      if (position > turn.start_ms) scrub.timer = window.setInterval(stepBack, SCRUB_TICK_MS)
    },
    [onScrubEnd]
  )

  const onJump = useCallback(
    (direction: 1 | -1) => {
      if (!activeTurn) return
      const turnIndex = orderedTurns.findIndex((turn) => turn.id === activeTurn.id)
      if (turnIndex < 0) return
      const currentAudio = audioRef.current
      const playheadOffset =
        currentAudio.state === 'present' && currentAudio.audioTime != null
          ? playheadCaret(activeTurn, activeTurn.text, currentAudio.audioTime)
          : null
      const focusedOffset = focusedStop?.turnId === activeTurn.id ? focusedStop.offset : null
      const cursorOffset =
        focusedOffset != null && playheadOffset != null
          ? Math.max(focusedOffset, playheadOffset)
          : (focusedOffset ?? playheadOffset ?? 0)
      const target = nextStop(stopsByTurnIndex, { turnIndex, offset: cursorOffset }, direction)
      if (!target) return

      const targetTurn = orderedTurns[target.turnIndex]
      onScrubEnd()
      setFocusedStop({ turnId: targetTurn.id, offset: target.offset })
      if (targetTurn.id !== activeTurn.id) {
        focusedByJumpRef.current = true
        selectedBySeekRef.current = true
        setActiveTurnId(targetTurn.id)
      }
      if (currentAudio.state === 'present') {
        const ms = offsetToMs(targetTurn, targetTurn.text, target.offset)
        pinnedTimeRef.current = ms
        currentAudio.playFrom(Math.max(0, ms - 500), targetTurn.end_ms + CLIP_PAD_MS)
      }
    },
    [activeTurn, focusedStop, onScrubEnd, orderedTurns, stopsByTurnIndex]
  )

  useEffect(
    () => () => {
      if (scrubRef.current?.timer != null) window.clearInterval(scrubRef.current.timer)
    },
    []
  )

  const onSplit = useCallback(() => {
    if (!activeTurnId) return
    if (textEditing) splitAtCaret()
    else setEditMode('split')
  }, [activeTurnId, textEditing, splitAtCaret])

  const dialogOpen =
    renameSpeakerId !== null ||
    assignTurnId !== null ||
    deleteSpeakerIds !== null ||
    roomDialog !== null ||
    shortcutsOpen ||
    finalize.confirmOpen ||
    discardConfirmOpen ||
    gtDocument.status === 'conflict'

  useEffect(() => {
    if (selectedRosterIds.size === 0 || dialogOpen) return
    const clearSelectionOutsideSpeakers = (event: MouseEvent) => {
      if (event.target instanceof Element && event.target.closest('[data-speaker-selection-target]')) return
      setSelectedSpeakerIds(new Set())
    }
    document.addEventListener('click', clearSelectionOutsideSpeakers)
    return () => document.removeEventListener('click', clearSelectionOutsideSpeakers)
  }, [selectedRosterIds.size, dialogOpen])

  const handlers: GtHotkeyHandlers = useMemo(
    () => ({
      onOk: onConfirm,
      onFix,
      onLabel,
      onDel: onDelete,
      onClear: () => {
        if (selectedRosterIds.size > 0) {
          requestDeleteSpeakers(Array.from(selectedRosterIds))
          return
        }
        onClear()
      },
      onEditText: () =>
        activeTurnId && startTextEdit(activeTurnId, focusedStop?.turnId === activeTurnId ? focusedStop.offset : null),
      onSplit,
      onAddSpeaker: () => setRosterAdding(true),
      onRoom,
      onNote: () => activeTurnId && setEditMode('note'),
      onRename: () => activeTurn?.speaker_id && setRenameSpeakerId(activeTurn.speaker_id),
      onUndo: () => dispatch({ type: 'undo' }),
      onRedo: () => dispatch({ type: 'redo' }),
      onNavigate: navigate,
      onJump,
      onPlayToggle: () => {
        if (!activeTurn) return
        if (audioRef.current.isPlaying) {
          pinnedTimeRef.current = audioRef.current.audioTime
          audioRef.current.togglePlayback()
          return
        }
        if (pinnedTimeRef.current != null) {
          audioRef.current.togglePlayback()
          return
        }
        playPadded(activeTurn)
      },
      onScrub,
      onScrubEnd,
      onRestart: () => activeTurn && playPadded(activeTurn),
      onSave: () => gtDocumentRef.current.flush(),
      onShortcuts: () => setShortcutsOpen(true),
    }),
    [
      onConfirm,
      onFix,
      onLabel,
      onDelete,
      onClear,
      onSplit,
      requestDeleteSpeakers,
      selectedRosterIds,
      onRoom,
      startTextEdit,
      onScrub,
      onScrubEnd,
      onJump,
      playPadded,
      activeTurnId,
      activeTurn,
      focusedStop,
      navigate,
    ]
  )

  useGtHotkeys({
    handlers,
    readOnly: state.readOnly,
    dialogOpen,
    enabled: !prefillQuery.isPending && seedState === null,
  })

  useEffect(() => {
    onScrubEnd()
    if (!focusedByJumpRef.current) setFocusedStop(null)
    focusedByJumpRef.current = false
    const turn = activeTurnRef.current
    if (!turn) return
    if (selectedBySeekRef.current) {
      selectedBySeekRef.current = false
      return
    }
    pinnedTimeRef.current = null
    playPadded(turn)
  }, [activeTurn?.id, onScrubEnd, playPadded])

  const rowEditor: GtRowEditor | undefined =
    editMode && activeTurn
      ? {
          turnId: activeTurn.id,
          mode: editMode,
          node: (
            <InlineTextField
              key={`${activeTurn.id}-${editMode}`}
              initialValue={textEditing ? activeTurn.text : (activeTurn.note ?? '')}
              multiline
              allowNewline={editMode === 'note'}
              maxLength={textEditing ? MAX_TURN_TEXT_CODEPOINTS : GT_NOTE_MAX_CODEPOINTS}
              ariaLabel={EDIT_MODE_LABEL[editMode]}
              variant={textEditing ? 'text' : 'note'}
              fieldRef={textEditing ? textFieldRef : undefined}
              onSplitRequest={textEditing ? splitAtCaret : undefined}
              splitOnEnter={editMode === 'split'}
              initialCaret={editMode === 'text' ? (editCaret ?? undefined) : undefined}
              cancelOnBlur={editMode === 'split'}
              onCommit={(value) => {
                if (textEditing) dispatch({ type: 'setText', turnId: activeTurn.id, text: value })
                else dispatch({ type: 'setNote', turnId: activeTurn.id, note: value })
                setEditMode(null)
              }}
              onCancel={() => setEditMode(null)}
            />
          ),
        }
      : undefined

  if (prefillQuery.isPending) {
    return <p className={styles.status}>Loading meeting…</p>
  }

  if (prefillQuery.isError || !prefillQuery.data) {
    return (
      <div className={styles.status}>
        <p>Couldn&apos;t load this meeting.</p>
        <Button title="Retry" type="light" onClick={() => void prefillQuery.refetch()} />
      </div>
    )
  }

  if (seedState !== null) {
    return (
      <div className={styles.status}>
        <SeedStatus
          state={seedState}
          prefillOrigin={prefillQuery.data.document.prefill_origin}
          checking={prefillQuery.isFetching}
          onCheckAgain={() => void prefillQuery.refetch()}
        />
      </div>
    )
  }

  return (
    <div className={styles.page}>
      <audio
        key={`${audio.audioUrl}:${audio.loadAttempt}`}
        ref={audio.audioRef}
        src={audio.audioUrl ?? undefined}
        preload="metadata"
        className={styles.audioElement}
        onLoadedMetadata={audio.onLoadedMetadata}
        onTimeUpdate={audio.onTimeUpdate}
        onSeeked={audio.onSeeked}
        onPlay={audio.onPlay}
        onPause={audio.onPause}
        onEnded={audio.onEnded}
        onError={audio.onError}
      />
      <GtHeader
        status={prefillQuery.data.status}
        version={state.version}
        prefillOrigin={prefillQuery.data.document.prefill_origin}
        progress={progress}
        saveStatus={gtDocument.status}
        errorReason={gtDocument.errorReason}
        onRetry={gtDocument.retry}
        discard={{
          busy: discardPending,
          disabled: state.readOnly || gtDocument.status === 'saving' || discardPending,
          error: discardError,
          onRequest: () => setDiscardConfirmOpen(true),
          onDismissError: () => setDiscardError(null),
        }}
        finalize={{ ...finalize, onReload: onReloadTheirs, onFocusTurn: focusTurn }}
      />
      {state.readOnly && <ReadOnlyCallout reason={prefillQuery.data.annotatable_reason} />}
      {prefillQuery.data.source === 's3' && (
        <FinalizedSourceCallout
          generation={prefillQuery.data.hydrated_from_generation ?? prefillQuery.data.finalized_generation ?? null}
        />
      )}
      {artifactChanged && <ArtifactChangedCallout />}
      {mirrorPrompt && (
        <MirrorRestoreCallout
          sameVersion={mirrorPrompt.sameVersion}
          onRestore={onMirrorRestore}
          onDiscard={onMirrorDiscard}
        />
      )}
      <GtContextStrip
        activeTurn={activeTurn}
        audio={audio}
        artifacts={artifacts}
        onSeek={onSeek}
        onPlayToggle={handlers.onPlayToggle}
      />
      <RosterBar
        speakers={state.doc.speakers}
        rooms={state.doc.rooms}
        turnCounts={turnCounts}
        speakerStyles={artifacts.speakerStyles}
        adding={rosterAdding}
        onStartAdd={() => setRosterAdding(true)}
        onAdd={(name) => {
          dispatch({ type: 'addSpeaker', name })
          setRosterAdding(false)
        }}
        onCancelAdd={() => setRosterAdding(false)}
        onRenameRequest={(speakerId) => setRenameSpeakerId(speakerId)}
        onDeleteRequest={(speakerId) => requestDeleteSpeakers([speakerId])}
        onToggleRecorder={(speakerId) => {
          const speaker = bySpeakerId.get(speakerId)
          dispatch({ type: 'setRecorder', speakerId: speaker?.is_recorder ? null : speakerId })
        }}
        selectedIds={selectedRosterIds}
        onToggleSelect={onToggleSelect}
        onGroupRoom={() => openGroupDialog(Array.from(selectedRosterIds))}
        onDeleteSelected={() => requestDeleteSpeakers(Array.from(selectedRosterIds))}
        onRenameRoom={openRenameRoomDialog}
        onUngroupRoom={(roomId) => dispatch({ type: 'ungroupRoom', roomId })}
        onLeaveRoom={(speakerId) => {
          const speaker = bySpeakerId.get(speakerId)
          if (speaker && roomDepartureHasNameCollision(state.doc.speakers, new Set([speakerId]))) {
            setRenameSpeakerId(speakerId)
            return
          }
          dispatch({ type: 'leaveRoom', speakerId })
        }}
        disabled={state.readOnly}
      />
      <div className={styles.controlsRow}>
        <div className={styles.toggleGroup}>
          <span className={styles.toggleLabel}>
            <Switch
              checked={needsAttentionOnly}
              onInputChange={setNeedsAttentionOnly}
              size="small"
              aria-label="Needs attention"
            />
            Needs attention
            <span className={clsx(styles.count, attentionTurnIds.size > 0 && styles.countPending)}>
              {attentionTurnIds.size}
            </span>
          </span>
          <span className={styles.toggleLabel}>
            <Switch checked={unreviewedOnly} onInputChange={setUnreviewedOnly} size="small" aria-label="Unreviewed" />
            Unreviewed
            <span className={clsx(styles.count, unreviewedTurnIds.size > 0 && styles.countPending)}>
              {unreviewedTurnIds.size}
            </span>
          </span>
          <span className={styles.toggleLabel}>
            <Switch checked={autoAdvance} onInputChange={setAutoAdvance} size="small" aria-label="Auto-advance" />
            Auto-advance
          </span>
        </div>
      </div>
      {prefillQuery.data?.annotatable && (
        <KeywordAssist
          status={keywordAssist.status}
          progress={keywordAssist.progress}
          failedCount={keywordAssist.failedCount}
          counts={keywordAssist.counts}
          onIdentify={() => keywordAssist.run(state.doc.turns, bySpeakerId)}
          onRetry={keywordAssist.retryFailed}
        />
      )}
      <GtTurnList
        turns={orderedTurns}
        speakersById={bySpeakerId}
        displayNames={displayNames}
        snapshotByTurnId={state.snapshotByTurnId}
        activeTurnId={activeTurnId}
        speakerStyles={artifacts.speakerStyles}
        editor={rowEditor}
        playheadMs={audio.state === 'present' ? audio.audioTime : null}
        keywordsBySourceTurnId={keywordAssist.keywordsBySourceTurnId}
        focusedStop={focusedStop}
        selectedSpeakerIds={selectedRosterIds}
        onToggleSpeaker={state.readOnly ? undefined : onToggleSelect}
        onSelect={(turnId) => {
          setSelectedSpeakerIds(new Set())
          setActiveTurnId(turnId)
        }}
        onReselect={() => {
          setSelectedSpeakerIds(new Set())
          handlers.onRestart()
        }}
        onAssignRequest={
          state.readOnly
            ? undefined
            : (turnId) => {
                setSelectedSpeakerIds(new Set())
                setActiveTurnId(turnId)
                setAssignTurnId(turnId)
              }
        }
        onEditRequest={state.readOnly ? undefined : startTextEdit}
      />
      <ActionBar
        activeTurn={activeTurn}
        snapshot={activeSnapshot}
        speaker={activeSpeaker}
        selectedCount={selectedRosterIds.size}
        hotkeySpeakers={hotkeySpeakers}
        displayNames={displayNames}
        speakerStyles={artifacts.speakerStyles}
        position={{
          index: activeTurnId ? orderedTurns.findIndex((turn) => turn.id === activeTurnId) : -1,
          total: orderedTurns.length,
        }}
        canUndo={state.undoStack.length > 0}
        readOnly={state.readOnly}
        audio={audio}
        onAssign={onAssign}
        onLabel={onLabel}
        onRoom={onRoom}
        onConfirm={onConfirm}
        onDelete={onDelete}
        onClear={onClear}
        onEditText={handlers.onEditText}
        onSplit={handlers.onSplit}
        splitHotkey={editMode === 'split' ? '⏎' : editMode === 'text' ? '⇧⏎' : null}
        onNote={handlers.onNote}
        onUndo={handlers.onUndo}
        onPlayToggle={handlers.onPlayToggle}
        onRestart={handlers.onRestart}
        onSeek={onSeek}
        onShortcuts={handlers.onShortcuts}
      />
      <ConflictDialog
        isOpen={gtDocument.status === 'conflict'}
        reason={gtDocument.conflict?.reason ?? 'conflict'}
        currentVersion={gtDocument.conflict?.currentVersion ?? null}
        onReloadTheirs={gtDocument.resolveConflictReload}
        onOverwriteMine={gtDocument.resolveConflictOverwrite}
        onCancel={gtDocument.resolveConflictCancel}
      />
      <ConfirmDialog
        isOpen={finalize.confirmOpen}
        title="Finalize this review"
        message="This saves the names and transcript to a permanent record. You can finalize again later to add a new version."
        confirmLabel="Finalize"
        confirmType="dark"
        onConfirm={finalize.confirm}
        onCancel={finalize.cancel}
      />
      <ConfirmDialog
        isOpen={discardConfirmOpen}
        title="Start over on this meeting?"
        message="This deletes the working draft, including every speaker and text edit that hasn't been finalized. The page reloads from the finalized record if one exists, otherwise from the consensus attribution."
        confirmLabel="Start over"
        confirmType="destructive"
        isConfirming={discardPending}
        onConfirm={onDiscardConfirm}
        onCancel={() => setDiscardConfirmOpen(false)}
      />
      <ConfirmDialog
        isOpen={deleteSpeakerIds !== null}
        title={
          deleteSpeakerIds?.length === 1
            ? `Delete "${displayNames.get(deleteSpeakerIds[0]) ?? bySpeakerId.get(deleteSpeakerIds[0])?.name ?? 'speaker'}"?`
            : `Delete ${deleteSpeakerIds?.length ?? 0} speakers?`
        }
        message={(() => {
          const count = liveTurnsOwnedBy(state.doc, deleteSpeakerIds ?? [])
          return `Their ${count} ${count === 1 ? 'turn becomes' : 'turns become'} unassigned and unreviewed. Press Z to undo.`
        })()}
        confirmLabel="Delete"
        confirmType="destructive"
        onConfirm={onDeleteSpeakersConfirm}
        onCancel={() => setDeleteSpeakerIds(null)}
      />
      <ShortcutsDialog isOpen={shortcutsOpen} close={() => setShortcutsOpen(false)} />
      <AssignSpeakerDialog
        isOpen={assignTurnId !== null}
        currentSpeaker={assignSpeaker}
        speakers={state.doc.speakers}
        displayNames={displayNames}
        close={() => setAssignTurnId(null)}
        onAssign={(speakerId) => {
          if (assignTurnId) onAssignToTurn(assignTurnId, speakerId)
          setAssignTurnId(null)
        }}
        onEditCurrentSpeaker={
          assignSpeaker && labelOfSpeaker(assignSpeaker) === null
            ? () => {
                setAssignTurnId(null)
                setRenameSpeakerId(assignSpeaker.id)
              }
            : undefined
        }
      />
      <RenameDialog
        isOpen={renameSpeakerId !== null}
        speaker={renameSpeakerId ? (bySpeakerId.get(renameSpeakerId) ?? null) : null}
        speakers={state.doc.speakers}
        rooms={state.doc.rooms}
        close={() => setRenameSpeakerId(null)}
        onRename={(speakerId, toName) => dispatch({ type: 'renameSpeaker', speakerId, name: toName })}
        onNameVoice={(speakerId, name) => dispatch({ type: 'nameRoomVoice', speakerId, name })}
        onJoinRoom={(speakerId, label) => {
          dispatch({ type: 'groupRoom', speakerIds: [speakerId], label })
          setSelectedSpeakerIds(new Set())
        }}
        onLeaveRoom={(speakerId) => dispatch({ type: 'leaveRoom', speakerId })}
        onDelete={(speakerId) => requestDeleteSpeakers([speakerId])}
      />
      <RoomDialog
        mode={roomDialog}
        rooms={state.doc.rooms}
        speakers={state.doc.speakers}
        close={() => setRoomDialog(null)}
        onSubmit={onRoomSubmit}
      />
    </div>
  )
}
